<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="refund-note">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="right-container">
      <div class="filter-bar">
        <div class="filter-description">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterOpen"
          :buttonIsDisabled="mainFilterDisable"
        >
          <div class="filter-popup">
            <BoxContainer
              title="Date"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="dateFilterChecked"
            >
              <div class="row-display">
                <DateTimePicker
                  :showTime="false"
                  placeholder="From"
                  :isDisabled="!dateFilterChecked"
                  :value.sync="fromRefundListingDate"
                  :errorMessage.sync="fromDateErrorMessage"
                />
                <DateTimePicker
                  :showTime="false"
                  placeholder="To"
                  :isDisabled="!dateFilterChecked"
                  :value.sync="toRefundListingDate"
                  :errorMessage.sync="toDateErrorMessage"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="searchFilterChecked"
            >
              <Dropdown
                placeholder="Search By"
                :items="searchFilterItems"
                itemKey="Search Criteria"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedSearchBy"
                :errorMessage.sync="searchByError"
              />
              <Dropdown
                placeholder="Search Criteria"
                :items="searchCriteriaItems"
                itemKey="name"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedCriteria"
              />
              <Input
                placeholder="Search"
                :inputValue.sync="searchByValue"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :errorMessage.sync="searchByValueError"
              />
            </BoxContainer>
            <button @click="refreshClicked()" :disabled="!dateFilterChecked && !searchFilterChecked">Refresh</button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="table-section">
        <TableBox
          title="Refund Note"
          :tableData="refundListData"
          :selectedRow.sync="selectedRefund"
          :loading="refundTableLoading"
          :hideColumns="['action', 'Inv No', 'Type']"
        />
      </div>
    </div>

    <PopupEntry
      v-if="popupShow"
      :show.sync="popupShow"
      :title="popupTitle"
      :createdBy="createdBy"
      updatedBy=""
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      @save="saveClicked"
      :viewMode="viewMode"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :requiredError="requiredError"
    >
      <!-- Summary of Events -->
      <BoxContainer v-if="action != 'N'" title="Summary of Events" :isCollapsible="true" :isCollapsed="isCollapsed">
        <div class="summary-container">
          <div class="summary-block" v-for="(m, i) in summaryItem" :key="i">
            <Tooltip class="milestone-tooltip" :minWidth="210" :tooltipText="m['Label'].join(',')">
              <div></div>
            </Tooltip>
            <div :id="m['Title']" class="summary-item"></div>
          </div>
        </div>
        <div class="summary-row" style="">
          <div v-for="(l, i) in Object.entries(newLegend)" :key="i" class="legend-items">
            <div class="legend-box" :style="{ backgroundColor: `${l[1]}` }"></div>
            {{ l[0] }}
          </div>
        </div>
      </BoxContainer>

      <!-- General Details -->
      <BoxContainer class="first-section" style="display: flex; margin: 10px 0px">
        <div style="display: flex; flex-direction: row; justify-content: space-between">
          <div class="top-row-column">
            <InputQuery
              ref="custno"
              :errorMessage.sync="customerNoErr"
              placeholder="Customer No"
              :isRequired="true"
              :isDisabled="viewMode && action != 'N'"
              :items="customersList"
              :selectedObject.sync="selectedCustomer"
              @selected="custSelected"
              itemKey="Customer Code"
              descKey="Customer Name"
              style="width: 100%"
            />
            <InputQuery
              ref="payType"
              :isRequired="true"
              :errorMessage.sync="paymentTypeErr"
              :items="paymentTypeList"
              itemKey="Payment Type"
              descKey="Description"
              :selectedObject.sync="selectedPaymentType"
              :isDisabled="viewMode && action != 'N'"
              placeholder="Payment Type"
            />
            <DateTimePicker
              :isRequired="true"
              :showTime="false"
              style="text-wrap: nowrap; margin-right: 5px; width: 70%"
              placeholder="Refund Date"
              :isDisabled="viewMode && action != 'N'"
              :value.sync="refundDate"
              :maxDate="new Date('2079-01-01')"
              :errorMessage.sync="refundDateErr"
              ref="refunddate"
            />
          </div>
          <div class="top-row-column">
            <InputQuery
              ref="bank"
              placeholder="Bank"
              :isDisabled="viewMode && action != 'N'"
              :items="bankList"
              :selectedObject.sync="selectedBank"
              itemKey="BankCode"
              descKey="BankName"
              :isRequired="bankChequeRequired || createChequeChecked"
              :errorMessage.sync="bankErr"
            />
            <BoxContainer
              title="Create Cheque"
              :isCheckBox="true"
              :isChecked.sync="createChequeChecked"
              :checkBoxIsDisabled="viewMode || (selectedPaymentType && selectedPaymentType['Payment Type'] == 'TX')"
            >
              <Input
                placeholder="Cheque No"
                :inputValue.sync="chequeNo"
                :isDisabled="(viewMode && action != 'N') || !createChequeChecked || chequeNoDisable"
                :errorMessage.sync="chequeNoErr"
                ref="chequeno"
                style="width: 47%"
                validator="text,25,,,spacedash"
                :isRequired="createChequeChecked"
              />
              <DateTimePicker
                :showTime="false"
                style="text-wrap: nowrap; margin-right: 5px; width: 70%"
                placeholder="Cheque Date"
                :isDisabled="(viewMode && action != 'N') || !createChequeChecked"
                :value.sync="chequeDate"
                ref="chequedate"
                :isRequired="bankChequeRequired || createChequeChecked"
                :errorMessage.sync="chequeDateErr"
                :maxDate="new Date('2079-01-01')"
              />
            </BoxContainer>
          </div>
          <div class="top-row-column">
            <RemarksBox
              :text.sync="remarks"
              :isDisabled="viewMode && action != 'N'"
              :maxLength="200"
              style="padding-bottom: 5px"
            />
          </div>
          <div class="top-row-column" style="margin-right: 0px; margin-top: 15px">
            <Input
              placeholder="Debit Note No"
              :inputValue.sync="debitNoteNo"
              :isDisabled="true"
              style="margin-bottom: 5px"
            />
            <Input
              placeholder="Payment Voucher No"
              :inputValue.sync="paymentVoucherNo"
              :isDisabled="true"
              style="margin-top: 5px"
            />
          </div>
        </div>
        <div style="margin-bottom: 5px"> </div>
      </BoxContainer>

      <!-- Tabs Section -->
      <Tab
        :tabs="tabsItems"
        :tabIndex.sync="currentTabIndex"
        style="width: 100%; height: 100%; overflow: auto"
        @tabClicked="tabClicked"
        :preventTabSwitch="true"
      >
        <!-- Details Tab -->
        <div slot="Refund Note Details" :style="{ display: 'flex', height: '100%' }">
          <BoxContainer class="table-container" :style="mainDetailsStyling">
            <div class="tabulator-title">
              <button @click="addNoteClicked" ref="addNote" v-if="!viewMode || action != ''" :disabled="showDetails"
                >Add</button
              >
              <button @click="deleteNoteClicked" v-if="!viewMode || action != ''" :disabled="showDetails"
                >Delete</button
              >
            </div>
            <TableBox
              title="Refund Note Details"
              v-if="showNotesTable"
              :selectedRow.sync="selectedNote"
              :tableData="notesList"
              :hideColumns="hideDetColumns"
              :columnWidth="['50', '135', '135', '135', '150', '100', '95', '400']"
            />
          </BoxContainer>

          <BoxContainer v-if="showDetails" class="table-container" :style="openAddNewStyling" ref="openAddNewStyling">
            <div class="tabulator-title" style="width: 100%">
              <div style="width: 50%">
                <InputQuery
                  :isDisabled="viewMode"
                  ref="receiptcredit"
                  :errorMessage.sync="selectedRcptCrErr"
                  placeholder="Receipt/Credit Note"
                  :isRequired="true"
                  :items="filteredReceiptCreditList"
                  :selectedObject.sync="selectedRcptCr"
                  itemKey="Ref No"
                  :showDesc="false"
                  @selected="receiptCreditSelected"
                />
              </div>
              <div style="display: flex; justify-content: end; width: 50%">
                <button style="margin-right: 3px" v-if="!viewMode" @click="saveNoteClicked">Add</button>
                <button v-if="!viewMode" @click="cancelNoteClicked">Cancel</button>
              </div>
            </div>

            <!-- Offset Amount / Currency Section -->
            <div class="foreign-currency-main">
              <div class="foreign-currency-controls">
                <!-- Header -->
                <div class="foreign-currency-row">
                  <div class="foreign-currency-column"></div>
                  <div class="foreign-currency-column"></div>
                  <div class="foreign-currency-column" :style="{ textAlign: 'center' }">MYR Equivalent</div>
                </div>

                <!-- Original Amount Row -->
                <BoxContainer background="rgba(225, 255, 255)" style="margin-bottom: 15px">
                  <div class="foreign-currency-row">
                    <div class="foreign-currency-column">
                      <InputQuery
                        placeholder="Currency"
                        :errorMessage.sync="noteCurrencyErr"
                        :showDesc="false"
                        :isDisabled="true"
                        :items="currencyList"
                        :selectedObject.sync="noteCurrency"
                        itemKey="Currency Code"
                        descKey="Description"
                      />
                    </div>
                    <div class="foreign-currency-column" style="display: flex">
                      <Input
                        placeholder="Amount"
                        :inputValue.sync="oriFAmount"
                        :isDisabled="true"
                        :errorMessage.sync="oriFAmountErr"
                        style="padding-right: 10px"
                      />
                      <Input
                        placeholder="Exch. Rate"
                        :isRequired="false"
                        :isDisabled="true"
                        :inputValue.sync="noteExchgRate"
                        validator="positiveDecimal,,3"
                        :errorMessage.sync="noteExchgRateErr"
                      />
                    </div>
                    <div class="foreign-currency-column">
                      <Input
                        placeholder="Amount"
                        :inputValue.sync="oriMYRAmount"
                        :isDisabled="true"
                        :errorMessage.sync="oriMYRAmountErr"
                      />
                    </div>
                  </div>
                </BoxContainer>

                <!-- Offset Amount Row -->
                <BoxContainer background="rgba(225, 255, 255)" style="margin-bottom: 15px">
                  <div class="foreign-currency-row">
                    <div class="foreign-currency-column">
                      <InputQuery
                        placeholder="Currency"
                        :errorMessage.sync="noteCurrencyErr"
                        :showDesc="false"
                        :isDisabled="true"
                        :items="currencyList"
                        :selectedObject.sync="noteCurrency"
                        itemKey="Currency Code"
                        descKey="Description"
                      />
                    </div>
                    <div class="foreign-currency-column" style="display: flex">
                      <!-- chg to computed func -->
                      <Input
                        placeholder="Offset Amount"
                        :isRequired="true"
                        :inputValue.sync="offsetFAmount"
                        :errorMessage.sync="offsetFAmountErr"
                        :isDisabled="viewMode || selectedRcptCr == null"
                        style="padding-right: 10px"
                        @focusChanged="updateFOffsetLossFocus"
                        validator="positiveDecimal,14,2"
                      />
                      <Input
                        placeholder="Exch. Rate"
                        :isDisabled="true"
                        :inputValue.sync="noteExchgRate"
                        validator="positiveDecimal,,3"
                        :errorMessage.sync="noteExchgRateErr"
                      />
                    </div>
                    <div class="foreign-currency-column">
                      <!-- chg to computed func -->
                      <Input
                        placeholder="Offset Amount"
                        :inputValue.sync="offsetMYRAmount"
                        :errorMessage.sync="offsetMYRAmountErr"
                        :isDisabled="viewMode || selectedRcptCr == null"
                        @focusChanged="updateOffsetLossFocus"
                        validator="positiveDecimal,14,2"
                      />
                    </div>
                  </div>
                </BoxContainer>
              </div>

              <div class="foreign-currency-background">
                <div class="background-left"></div>
                <div class="background-middle"></div>
                <div class="background-right"></div>
              </div>
            </div>
          </BoxContainer>
        </div>

        <!-- Attachment Tab -->
        <div slot="Attachments" style="height: 95%; width: 100%; overflow: scroll">
          <div style="display: flex; justify-content: end; margin-bottom: 6px">
            <button v-if="!viewMode" style="margin-right: 6px" @click="showAttachmentsPopup = true"
              >+ Add Attachement</button
            >
            <button v-if="!viewMode" @click="deleteDocClicked">- Del Attachement</button>
            <!-- <button v-if="viewMode"  style="margin-right: 6px" @click="viewDocClicked">View Attachement</button> -->
          </div>
          <TableBox
            v-if="showNotesTable"
            title="Attachment"
            :tableData="documentsList"
            :selectedRow.sync="selectedDoc"
            :additionalButtons="viewMode ? viewButton : []"
            @buttonClicked="viewDocClicked"
            :hideColumns="[
              'Action',
              'Doc Image',
              'Sequence No',
              'Document GUID',
              'ID',
              'Doc Ref No',
              'Job No',
              'Doc Code',
              'Doc Type',
              'Doc Rec Date',
              'Doc Rec Time',
              '_attachmentAction',
            ]"
          />
        </div>

        <!-- Events Tab -->
        <div slot="Events">
          <div>
            <div>
              <BoxContainer title="Show Details">
                <div style="display: flex">
                  <CheckBoxV2 :isChecked.sync="showGLEntry" title="GL Entry" />
                  <CheckBoxV2 :isChecked.sync="showeInvoice" title="eInvoice Submission" />
                </div>
              </BoxContainer>
            </div>
            <div style="display: flex; flex-direction: row; margin-top: 10px">
              <BoxContainer
                v-if="showGLEntry"
                title="GL Entry"
                :style="{ width: '100%', marginTop: '0px', marginRight: showGLEntry && !showeInvoice ? '' : '5px' }"
              >
                <div>
                  <TableBox title="GL Entry" :tableData="glEntryList" :hideColumns="[]" />
                </div>
                <div style="display: flex; padding-top: 10px; justify-content: space-around">
                  <div style="display: flex">
                    <div
                      style="
                        min-width: 150px;
                        max-width: 150px;
                        padding: 3px;
                        margin-top: 1px;
                        margin-right: 6px;
                        font-size: 1.1em;
                      "
                      >TOTAL DEBIT</div
                    >
                    <div class="total-block" style="min-width: 150px">{{ glDebitTotal }}</div>
                  </div>
                  <div style="display: flex">
                    <div
                      style="
                        min-width: 150px;
                        max-width: 150px;
                        padding: 3px;
                        margin-top: 1px;
                        margin-right: 6px;
                        font-size: 1.1em;
                      "
                      >TOTAL CREDIT</div
                    >
                    <div class="total-block" style="min-width: 150px">{{ glCreditTotal }}</div>
                  </div>
                </div>
              </BoxContainer>
              <BoxContainer v-if="showeInvoice" title="LHDN Submission" style="width: 100%; margin-top: 0px">
                <TableBox title="eInvoice Submission" :tableData="eInvoiceList" :maxSelection="0" />
              </BoxContainer>
            </div>
          </div>
        </div>
      </Tab>

      <!-- Total Amount Section -->
      <BoxContainer>
        <div style="display: flex; flex-direction: row; justify-content: space-between">
          <div style="font-size: 1.2em">{{ totalInWords }} ONLY</div>
          <div style="display: flex; flex-direction: row">
            <div
              style="
                min-width: 150px;
                max-width: 150px;
                padding: 3px;
                margin-top: 1px;
                margin-right: 6px;
                font-size: 1.1em;
              "
              >TOTAL REFUND</div
            >
            <div class="total-block" style="font-size: 1.1em"
              >{{ totalFAmount }} {{ refundCurrency ? refundCurrency['Currency Code'] : 'MYR' }}</div
            >
            <div
              class="total-block"
              style="background-color: #467cdc; color: white; font-size: 1.1em; font-weight: bold"
              >{{ totalMYRAmount }} MYR</div
            >
          </div>
        </div>
      </BoxContainer>
    </PopupEntry>
    <PopupV2
      :show="showDeleteRefund"
      title="Delete Refund Note"
      @buttonClicked="deletePopupClicked"
      :buttons="deletePopupButtons"
    >
      <div>Please state reason to delete {{ selectedRefund.length > 0 ? selectedRefund[0]['Refund #'] : '' }}?</div>
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="deleteRefundRemarks"
        :errorMessage.sync="deleteRefundRemarksErr"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>
    <div class="attachments-popup-container">
      <!-- Since refund note doesn't allow edit, don't need cater  -->
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        title="Add Attachment"
        :buttons="attachmentPopupBtns"
        @buttonClicked="attachmentBtnsClicked"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Select File"
            ref="docFile"
            :isDisabled="viewMode"
            :inputValue.sync="documents['docName']"
            :errorMessage.sync="attachmentsRequiredErrorMessage"
            @fileChanged="handleFileUpload"
          />
          <RemarksBox
            :maxLength="200"
            style="height: 50%"
            title="Remarks"
            :isDisabled="viewMode"
            :text.sync="documents['remarks']"
          />
        </div>
      </PopupV2>
    </div>
    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="isProjectable"
          :data="attachmentProjectorData"
          :type="attachmentProjectorType"
          draggable="false"
        ></object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{ selectedDoc[0]['File Type'].replace('.', '').toUpperCase() }} files cannot be previewed.<br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector"> &times; </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},

  computed: {
    glDebitTotal: function () {
      let total = 0;
      if (this.glEntryList.length > 0) {
        this.glEntryList.forEach((gld) => {
          if (gld['Debit No'] != '0') {
            total += Number(gld['Amount'].replaceAll(',', ''));
          }
        });
      }
      return this.$currencyFormat(total);
    },
    glCreditTotal: function () {
      let total = 0;
      if (this.glEntryList.length > 0) {
        this.glEntryList.forEach((gld) => {
          if (gld['Credit No'] != '0') {
            total += Number(gld['Amount'].replaceAll(',', ''));
          }
        });
      }
      return this.$currencyFormat(total);
    },
    filteredReceiptCreditList: function () {
      let saved = [];
      this.notesList.forEach((n) => {
        saved.push(n['Type'] === 'RT' ? n['Receipt No'] : n['Credit No']);
      });
      return this.rcptCrList.filter((rc) => !saved.includes(rc['Ref No']));
    },

    //#region Stylings
    mainDetailsStyling: function () {
      return {
        width: this.showDetails ? '50%' : '100%',
        marginRight: '3px',
        height: '100%',
      };
    },

    openAddNewStyling: function () {
      return {
        width: this.showDetails ? '50%' : '0%',
        height: 'fit-content',
        overflow: 'scroll',
        height: this.notesList.length == 0 ? '100%' : 'max-content',
      };
    },
    //#endregion
  },

  data: function () {
    return {
      menu: [
        { title: 'New Refund Note', icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png' }, //, enabled: true
        { title: 'Delete Refund Note', icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png' }, //, enabled: false, key: 'mnuDelete'
        { title: 'View Refund Note', icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png' }, //, enabled: false, key: 'mnFind'
      ],
      currentTabIndex: 0,
      tabsItems: [
        { title: 'Refund Note Details', disabled: false },
        { title: 'Attachments', disabled: false },
        { title: 'Events', disabled: false },
      ],
      summaryItem: [
        {
          Title: 'Indicator',
          Label: ['New', 'Cancelled'],
          Indicator: [
            { code: 'N', name: 'New Refund Note', bg: '#ffff99' },
            { code: 'C', name: 'Cancelled', bg: 'FFFFFF' },
          ],
        },
        {
          Title: 'LHDN Submission',
          Label: ['LHDN Queue', 'LHDN Sent', 'LHDN Accepted', 'LHDN Rejected', 'LHDN Cancelled', 'LHDN Pending'],
          Indicator: [
            { code: 'Q', name: 'LHDN Queue', bg: '#ffff99' },
            { code: 'S', name: 'LHDN Sent', bg: '#ffff99' },
            { code: 'A', name: 'LHDN Accepted', bg: '#79d279' },
            { code: 'R', name: 'LHDN Rejected', bg: '#ff8787' },
            { code: 'C', name: 'LHDN Cancelled', bg: 'FFFFFF' },
            { code: 'P', name: 'LHDN Pending', bg: '#ffff99' },
          ],
        },
      ],
      newLegend: {
        Completed: '#79d279',
        Pending: '#ffff99',
      },

      mainFilterOpen: false,
      mainFilterDisable: false,
      filterText: '',
      lastRefreshed: '',
      dateFilterChecked: true,
      searchFilterChecked: false,
      fromRefundListingDate: new Date(),
      toRefundListingDate: new Date(),
      fromDateErrorMessage: '',
      toDateErrorMessage: '',
      searchFilterItems: [],
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedCriteria: null,
      selectedSearchBy: null,
      searchByError: '',
      searchByValue: '',
      searchByValueError: '',

      refundTableLoading: false,
      hideDetColumns: [],
      refundListData: [],
      selectedRefund: [],

      popupShow: false,
      popupTitle: '',
      viewMode: false,
      isCollapsed: false,
      action: '',
      createdBy: '',
      deletePopupButtons: [
        { text: 'Delete', color: 'indianred', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      showDeleteRefund: false,
      deleteRefundRemarks: '',
      deleteRefundRemarksErr: '',

      successMessages: [],
      errorMessages: [],
      requiredError: [],

      //#region Refund Notes Details data
      refundDetails: [],
      customersList: [],
      previousSelectedCust: null,
      selectedCustomer: null,
      customerNoErr: '',
      refundDate: null,
      refundDateErr: '',
      paymentTypeList: [],
      selectedPaymentType: null,
      paymentTypeErr: '',
      bankList: [],
      selectedBank: null,
      bankErr: '',
      createChequeChecked: false,
      chequeNo: '',
      chequeNoErr: '',
      chequeNoDisable: false,
      chequeDate: null,
      chequeDateErr: '',
      bankChequeRequired: false,
      debitNoteNo: '',
      paymentVoucherNo: '',
      totalInWords: '',
      totalMYRAmount: '',
      totalFAmount: '',
      refundCurrency: '',
      remarks: '',
      //#endregion

      //#region Refund Notes Tab Data
      showDetails: false,
      //Lists
      notesList: [],
      currencyList: [],
      rcptCrList: [],

      showNotesTable: true,
      selectedNote: [],
      selectedRcptCr: null,
      selectedRcptCrErr: '',

      noteCurrency: null,
      noteCurrencyErr: '',
      noteExchgRate: '',
      noteExchgRateErr: '',
      oriFAmount: '',
      oriFAmountErr: '',
      oriMYRAmount: '',
      oriMYRAmountErr: '',

      offsetFAmount: '',
      offsetFAmountErr: '',
      offsetMYRAmount: '',
      offsetMYRAmountErr: '',

      currFactor: '',
      //#endregion

      //#region Attachments Tab Data
      attachmentAction: '',
      attachmentPopupTitle: '',
      attachmentPopupBtns: [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }],
      viewButton: [{ text: 'View', index: 0, width: 60 }],
      showAttachmentsPopup: false,
      showAttachmentProjector: false,
      isProjectable: false,
      attachmentsRequiredErrorMessage: '',
      documentsList: [],
      documents: {
        docName: '',
        attachmentFile: [],
        fileType: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      },
      selectedDoc: [],
      selectedDocIndex: -1,
      //#endregion

      //#region attachment projector data fields
      attachmentProjectorFileName: '',
      attachmentProjectorType: '',
      attachmentProjectorData: '',
      //#endregion

      //#region Events Tab Data
      showGLEntry: true,
      showeInvoice: true,
      glEntryList: [],
      eInvoiceList: [],
      //#endregion
    };
  },

  props: {},

  methods: {
    menuClicked: async function (e) {
      this.clearAllData();
      this.clearAllErr();
      this.mainFilterOpen = false;

      switch (e['title']) {
        case 'New Refund Note':
          this.hideDetColumns = ['action', 'Inv No', 'Type', 'Remarks'];
          this.action = 'N';
          this.getDetails(e['title']);
          this.debitNoteNo = '**********';
          this.paymentVoucherNo = '**********';
          this.refundDate = new Date();
          this.popupShow = true;
          this.viewMode = true;
          this.createdBy = '';
          this.currentTabIndex = 0;
          this.totalMYRAmount = '0.00';
          this.totalFAmount = '0.00';
          this.showDetails = false;
          this.$nextTick(() => {
            this.$refs['custno'].setFocus();
          });
          break;

        case 'Delete Refund Note':
          if (this.selectedRefund.length == 0) {
            this.$showAlert('error', 'Delete Refund Note', 'Please select a record to delete');
            return;
          }
          if (this.selectedRefund[0]['Indicator'] === 'C') {
            this.$showAlert(
              'error',
              'Selected Refund Note Already Cancelled',
              'You are not allowed to delete cancelled refund note!',
            );
            return;
          }
          this.action = 'D';
          this.deleteRefundRemarks = '';
          this.deleteRefundRemarksErr = '';
          this.showDeleteRefund = true;

          break;

        case 'View Refund Note':
          if (this.selectedRefund.length == 0) {
            this.$showAlert('error', 'View Refund Note', 'Please select a record to view');
            return;
          }
          this.action = '';
          this.hideDetColumns = ['action', 'Inv No', 'Type'];
          this.viewMode = true;
          this.getDetails(e['title']);
          this.popupShow = true;
          break;
      }
    },

    refreshClicked: async function () {
      let errorRefundListing = false;
      if (this.dateFilterChecked) {
        if (!this.fromRefundListingDate) {
          this.fromDateErrorMessage = 'Enter from date!';
          errorRefundListing = true;
        }

        if (!this.toRefundListingDate) {
          this.toDateErrorMessage = 'Enter to date!';
          errorRefundListing = true;
        }
      }

      if (errorRefundListing) return;

      this.mainFilterOpen = false;
      let dataToPost = null;

      dataToPost = {
        startdate: this.dateFilterChecked ? this.fromRefundListingDate?.formatString('yyyy-MM-dd') : '',
        enddate: this.dateFilterChecked ? this.toRefundListingDate?.formatString('yyyy-MM-dd') : '',
        txntype: '1',
        searchfield: this.searchFilterChecked ? this.selectedSearchBy['Search Criteria'] : '',
        searchcriteria: this.searchFilterChecked ? this.selectedCriteria['name'] : '',
        searchvalue: this.searchFilterChecked ? this.searchByValue : '',
      };

      this.filterText = 'Filter by ';
      if (this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}, from ${this.fromRefundListingDate.formatString('yyyy-MM-dd')} to ${this.toRefundListingDate.formatString('yyyy-MM-dd')}`;
      } else if (this.dateFilterChecked && !this.searchFilterChecked) {
        this.filterText = `Filter by date from ${this.fromRefundListingDate.formatString('yyyy-MM-dd')} to ${this.toRefundListingDate.formatString('yyyy-MM-dd')}`;
      } else if (!this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}`;
      }

      this.$fullscreenLoading('Getting Refund Note Listing');
      this.refundTableLoading = true;
      this.refundListData = [];
      const getRefunds = this.$apiCallV2('/RefundNote/GetRefundListing', dataToPost).then((result) => {
        this.refundListData = result;
        this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
        this.refundTableLoading = false;
        this.$fullscreenLoading();
      });
      return getRefunds;
    },

    getDetails: async function (title) {
      this.popupTitle = title;
      if (this.selectedRefund.length > 0 && this.action != 'N') {
        let debitNo = this.selectedRefund[0]['Refund #'];
        await this.populateRefundDetails(debitNo);
        this.loadEventOfSummary();
      }
    },

    populateRefundDetails: async function (refundNo) {
      this.$fullscreenLoading('Getting data');
      this.refundDetails = [];
      this.refundDetails = (
        await this.$apiCallV2('/Master/GetDetailsMaster', {
          masterfile: 'refundnote',
          recordid: refundNo,
          module: 'MASTER',
        })
      )['Refund'];

      let userCreated = this.refundDetails['CreatedBy'] || '';
      // let dateCreated = this.refundDetails['DateCreated'] ? new Date(this.refundDetails['DateCreated']).formatString('dd-MM-yyyy') : "";
      this.createdBy = `${userCreated} ${new Date(this.refundDetails['DateCreated']).formatString('dd-MM-yyyy HH:mm')}`;
      this.selectedCustomer = this.customersList.find((c) => c['Customer Code'] == this.refundDetails['Customer Code']);
      this.selectedPaymentType = this.paymentTypeList.find(
        (p) => p['Payment Type'] == this.refundDetails['Payment Type'],
      );
      this.refundDate = new Date(this.refundDetails['Date']);
      this.selectedBank = this.bankList.find((b) => b['BankCode'] == this.refundDetails['Bank']) || null;
      this.chequeNo = this.refundDetails['Cheque No'] || '';
      this.chequeDate = this.refundDetails['Cheque Date'] ? new Date(this.refundDetails['Cheque Date']) : null;
      this.createChequeChecked = this.refundDetails['createcheque'] == '1' ? true : false;
      this.remarks = this.refundDetails['Remark'] || '';
      this.debitNoteNo = this.refundDetails['Debit Note No'] || '';
      this.paymentVoucherNo = this.refundDetails['Payment Voucher No'] || '';
      // this.totalMYRAmount = this.refundDetails['Amount'] || "0.00";
      // this.totalFAmount = this.refundDetails['F.Amount'] || "0.00";
      this.notesList = this.refundDetails['RefundDetails'] || [];
      if (this.notesList.length > 0) {
        this.notesList.forEach((note) => {
          note['Offset Amount'] = this.$currencyFormat(note['Offset Amount']);
          note['F.Offset Amount'] = this.$currencyFormat(note['F.Offset Amount']);
        });
      }
      this.updateRefundTotal();
      this.updateRefundForeignTotal();
      this.refundCurrency = this.currencyList.find((c) => c['Currency Code'] == this.refundDetails['Currency']) || null;
      this.documentsList = this.refundDetails['Attachments'] || [];
      if (this.documentsList.length != 0) {
        this.documentsList.forEach((d) => {
          d['Doc Date'] = new Date(d['Doc Date']).formatString('yyyy-MM-dd HH:mm:ss');
        });
      }
      this.eInvoiceList = this.refundDetails['eInvoice'] || [];
      this.glEntryList = this.refundDetails['GL'] || [];

      this.$fullscreenLoading();
    },

    loadEventOfSummary: function () {
      if (this.eInvoiceList.length > 0) {
        let getLatestObj = this.eInvoiceList.reduce((acc, item) => {
          return item['#'] > acc['#'] ? item : acc;
        });

        this.refundDetails['LHDN Submission'] = getLatestObj['Status'][0];
      } else {
        this.refundDetails['LHDN Submission'] = 'P';
      }

      for (let key in this.refundDetails) {
        if (this.refundDetails.hasOwnProperty(key)) {
          let indicatorName = this.getIndicatorName(key, this.refundDetails[key], 'title');
          let backgroundColor = this.getIndicatorName(key, this.refundDetails[key], 'background');
          let element = document.getElementById(key);
          if (element) {
            element.textContent = indicatorName;
            element.style.backgroundColor = backgroundColor;
          }
        }
      }
    },

    getIndicatorName: function (key, value, type) {
      let item = this.summaryItem.find((item) => item.Title === key);

      if (item && item.Indicator) {
        let indicator = item.Indicator.find((indicator) => indicator.code === value);
        if (indicator) {
          return type == 'title' ? indicator.name : indicator.bg;
        }
      }
      return type == 'title' ? item?.Indicator?.[0]?.name || null : item?.Indicator?.[0]?.bg || '#ffffff';
    },

    requiredClicked: function (item) {
      if (item == 'addNote') {
        this.showDetails = true;
        this.$nextTick(() => {
          this.$refs['receiptcredit'].setFocus();
        });
      } else {
        this.$nextTick(() => {
          this.$refs[item].setFocus();
        });
      }
    },

    checkPopupEntryFields: async function () {
      this.clearAllErr();
      let error = false;
      if (this.selectedCustomer == null) {
        this.customerNoErr = 'Customer No is Required!';
        this.requiredError.push({ key: 'custno', value: this.customerNoErr });
        error = true;
      }

      if (this.selectedPaymentType == null) {
        this.paymentTypeErr = 'Payment Type is Required!';
        this.requiredError.push({ key: 'payType', value: this.paymentTypeErr });
        error = true;
      }

      if (!this.refundDate) {
        this.refundDateErr = 'Refund Date is Required!';
        this.requiredError.push({ key: 'refunddate', value: this.refundDateErr });
        error = true;
      } else if (
        this.refundDate &&
        this.$dateTime.stringToDate(this.refundDate.formatString('dd/MM/yyyy'), '/') > new Date('2079-06-06')
      ) {
        this.refundDateErr = 'Refund Date Invalid!';
        this.requiredError.push({ key: 'refunddate', value: this.refundDateErr });
        error = true;
      }

      if (this.notesList.length === 0) {
        this.requiredError.push({
          key: 'addNote',
          value: 'Please select at least one Receipt/Credit Note for refund.',
        });
        error = true;
      }

      if (this.createChequeChecked && !this.chequeNo.trim()) {
        this.chequeNoErr = 'Cheque No is Required!';
        this.requiredError.push({ key: 'chequeno', value: this.chequeNoErr });
        error = true;
      }

      if (this.chequeNo.trim().length > 25) {
        this.chequeNoErr = 'Cheque No cannot exceed 25 characters!';
        this.requiredError.push({ key: 'chequeno', value: this.chequeNoErr });
        error = true;
      }

      if ((this.bankChequeRequired || this.createChequeChecked) && !this.selectedBank) {
        this.bankErr = 'Bank is required!';
        this.requiredError.push({ key: 'bank', value: this.bankErr });
        error = true;
      }

      if ((this.bankChequeRequired || this.createChequeChecked) && !this.chequeDate) {
        this.chequeDateErr = 'Cheque Date is required!';
        this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
        error = true;
      }

      if (
        this.chequeDate &&
        this.$dateTime.stringToDate(this.chequeDate.formatString('dd/MM/yyyy'), '/') > new Date('2079-06-06')
      ) {
        this.chequeDateErr = 'Cheque Date Invalid!';
        this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
        error = true;
      } else if (this.chequeDate && this.chequeDate < new Date(this.refundDate).setHours(0, 0, 0, 0)) {
        this.chequeDateErr = 'Cheque Date Cannot Be Earlier than Refund Date!';
        this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
        error = true;
      }

      return error;
    },

    saveClicked: async function () {
      const isIValid = await this.checkPopupEntryFields();

      if (isIValid) return;

      if (Number(this.totalFAmount) == 0) {
        this.errorMessages.push('Total foreign amount must be more than 0 for refund');
        return;
      }

      if (Number(this.totalMYRAmount) == 0) {
        this.errorMessages.push('Total amount must be more than 0 for refund');
        return;
      }

      this.$fullscreenLoading('Validating Transactions');
      const refreshedRcptCrd = await this.$apiCallV2(
        `/RefundNote/GetReceiptCreditListing?custid=${this.selectedCustomer['Customer Code']}`,
      );
      for (const n of this.notesList) {
        let note = null;
        const key = n['Type'] == 'RT' ? 'Receipt No' : 'Credit No';
        note = refreshedRcptCrd.find((rc) => rc['Ref No'] == n[key]);

        if (
          !note ||
          (note &&
            (Number(note['F Balance']) < Number(n['F.Offset Amount']) ||
              Number(note['Balance']) < Number(n['Offset Amount'])))
        ) {
          this.errorMessages.push(`Offset Amount for ${n[key]} exceeded the outstanding amount`);
          return;
        }
      }
      this.$fullscreenLoading();

      if (this.showDetails) {
        const discardChanges = await this.promptDiscardReceiptCredit();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showDetails = false;
      }
      this.clearRefundDetails();

      let attToSend = this.documentsList.map((d) => {
        return {
          doccode: 'ATT',
          action: d['Action'],
          docrefno: '',
          remarks: d['Doc Remark'] ?? '',
          status: '',
          docname: d['File Name'] ?? '',
          docimage: d['Doc Image'] ?? '',
          docimagetype: d['File Type'],
          docguid: this.action == 'N' ? '' : d['Document GUID'],
        };
      });

      let notesToSend = this.notesList.map((n) => {
        return {
          action: n['action'],
          seqno: n['#'] || '', // backend or frontend pass
          chargescode: '',
          txncode: '',
          jobno: '',
          amount: n['Offset Amount'] ?? '',
          tabsource: '',
          adjamt: n['Offset Amount'] ?? '',
          taxableamt: '',
          netamt: n['Offset Amount'] ?? '',
          famount: n['F.Offset Amount'] ?? '',
          fadjamt: n['F.Offset Amount'] ?? '',
          ftaxableamt: '',
          fnetamt: n['F.Offset Amount'] ?? '',
          exrate: n['Ex. Rate'] ?? '',
          currency: n['Currency'] ?? '',
          tax: '',
          remark1: n['Remarks'] ?? '',
          contno: '',
          payable: '',
          payamount: n['Offset Amount'] ?? '',
          payfamount: n['F.Offset Amount'] ?? '',
          statusremark: '',
          indicator: '',
          apinvno: '',
          refno: n['Type'] == 'RT' ? n['Receipt No'] : n['Credit No'],
          txntype:
            n['Type'] ?
              n['Type'] == 'RT' ?
                'R'
              : 'C'
            : '',
        };
      });

      let finalExchgRate = (
        Number(this.totalMYRAmount.replaceAll(',', '')) / Number(this.totalFAmount.replaceAll(',', ''))
      ).toFixed(2);
      let dataToSave = {
        action: 'N',
        invno: '',
        custno: this.selectedCustomer ? this.selectedCustomer['Customer Code'] : '',
        invdate: new Date(this.refundDate).formatString('yyyy-MM-dd'),
        invjobtype: '',
        total: this.totalMYRAmount || '',
        remark1: this.remarks || '',
        remark2: '',
        remark3: '',
        adv: '',
        txntype: '1',
        credittype: '',
        creditterm: '',
        duedate: '',
        adjentry: '',
        reqposting: '',
        authorized: '',
        filler: '',
        exrate: finalExchgRate,
        currency: this.refundCurrency ? this.refundCurrency['Currency Code'] : '',

        paymentno: '',
        payment: '',
        paytype: this.selectedPaymentType ? this.selectedPaymentType['Payment Type'] : '',
        payeetype: '',
        payeecode: this.selectedCustomer ? this.selectedCustomer['Customer Code'] : '',
        payee: this.selectedCustomer ? this.selectedCustomer['Customer Name'] : '',
        remark: '',
        ftotal: this.totalFAmount || '',
        indicator: 'N',
        contra: '2',
        createcheque: this.createChequeChecked ? '1' : '0',
        chqno: this.createChequeChecked ? this.chequeNo : '',
        bankcode: this.selectedBank ? this.selectedBank['BankCode'] : '',
        chqdate: this.createChequeChecked ? new Date(this.chequeDate).formatString('yyyy-MM-dd') : '',
        reason: '',
        details: notesToSend,
        attachments: attToSend,
      };

      this.$fullscreenLoading('Saving Refund Note');
      let result = await this.$apiCallV2('/RefundNote/SaveRefundNote', dataToSave);
      this.$fullscreenLoading();

      if (result[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating Refund Note: ${result[0]['ErrMsg']}`);
        }
        return;
      }

      let debitNo = result[0]['Debit Note No'];

      this.showNotesTable = false;
      this.clearAllErr();
      this.$nextTick(async () => {
        await this.populateRefundDetails(debitNo);
        this.loadEventOfSummary();
        this.showNotesTable = true;
        await this.refreshClicked();
      });

      if (this.action == 'N') {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      } else {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      }
      this.viewMode = true;
      this.popupTitle = 'View Refund Note';
      this.action = '';
    },

    deletePopupClicked: async function (e) {
      switch (e) {
        case 'Delete':
          if (this.deleteRefundRemarks.trim().length == 0) {
            this.deleteRefundRemarksErr = 'Please enter delete remarks!';
            return;
          }
          this.$fullscreenLoading('Deleting Selected Refund Note');
          let deleteBody = {
            action: 'D',
            invno: this.selectedRefund[0]['Refund #'],
            paymentno: this.selectedRefund[0]['Payment Voucher No'],
            reason: this.deleteRefundRemarks,
          };
          let deleteResult = await this.$apiCallV2('/RefundNote/SaveRefundNote', deleteBody);
          this.$fullscreenLoading();
          this.showDeleteRefund = false;
          if (deleteResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Deleted!',
              `Refund Note ${this.selectedRefund[0]['Refund #']} was successfully deleted.`,
            );
            this.refundListData = [];
            this.selectedRefund = [];
            this.$nextTick(async () => await this.refreshClicked());
          } else {
            this.$showAlert('error', 'Delete unsuccessful', `${deleteResult[0]['ErrMsg']}`);
            this.refundListData = [];
            this.selectedRefund = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;

        case 'Cancel':
          this.deleteRefundRemarks = '';
          this.showDeleteRefund = false;
          break;
      }
    },

    clearAllData: function () {
      this.popupTitle = '';
      this.createdBy = '';
      this.successMessages = [];

      this.selectedCustomer = null;
      (this.refundDate = null), (this.selectedPaymentType = null);
      this.selectedBank = null;
      this.createChequeChecked = false;
      this.chequeNo = '';
      this.chequeDate = null;
      this.debitNoteNo = '';
      this.paymentVoucherNo = '';
      this.totalInWords = '';
      this.totalMYRAmount = '';
      this.totalFAmount = '';
      this.remarks = '';
      this.refundCurrency = '';
      this.notesList = [];
      this.rcptCrList = [];
      this.selectedNote = [];
      this.currFactor = '';

      this.documentsList = [];
      this.selectedDoc = [];
      this.selectedDocIndex = -1;
      (this.documents = {
        docName: '',
        attachmentFile: [],
        fileType: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      }),
        (this.glEntryList = []);

      this.clearRefundDetails();
    },

    clearAllErr: function () {
      this.errorMessages = [];
      this.requiredError = [];

      this.customerNoErr = '';
      this.paymentTypeErr = '';
      this.chequeNoErr = '';
      this.selectedRcptCrErr = '';
      this.noteCurrencyErr = '';
      this.oriFAmountErr = '';
      this.noteExchgRate = '';
      this.oriMYRAmountErr = '';
      this.offsetFAmountErr = '';
      this.offsetMYRAmountErr = '';
      this.bankErr = '';
      this.chequeDateErr = '';
      this.refundDateErr = '';
    },

    clearRefundDetails: function () {
      this.selectedRcptCr = null;
      this.noteCurrency = null;
      this.noteExchgRate = '';
      this.oriFAmount = '';
      this.oriMYRAmount = '';

      this.offsetFAmount = '';
      this.offsetMYRAmount = '';

      this.oriFAmountErr = '';
      this.oriMYRAmountErr = '';
      this.offsetFAmountErr = '';
      this.offsetMYRAmountErr = '';
    },
    updateRefundTotal: function () {
      let total = 0;
      if (this.notesList.length > 0) {
        this.totalMYRAmount = 0.0;
        this.notesList.forEach((note) => {
          total += Number(note['Offset Amount'].replaceAll(',', ''));
        });
      }
      this.totalMYRAmount = this.$currencyFormat(total);
    },
    updateRefundForeignTotal: function () {
      let total = 0;
      if (this.notesList.length > 0) {
        this.totalFAmount = 0.0;
        this.notesList.forEach((note) => {
          total += Number(note['F.Offset Amount'].replaceAll(',', ''));
        });
      }
      this.totalFAmount = this.$currencyFormat(total);
    },

    //#region Receipt/Credit Notes Tab
    addNoteClicked: async function () {
      if (!this.selectedCustomer) {
        await this.$showAlert('warning', 'No Customer is Selected', 'Please select a customer');
        this.$nextTick(() => {
          this.$refs['custno'].setFocus();
        });
        return;
      }
      this.showDetails = true;
      this.$nextTick(() => {
        this.$refs['receiptcredit'].setFocus();
      });
    },

    deleteNoteClicked: async function () {
      if (this.selectedNote.length == 0) {
        this.$showAlert('error', 'No Receipt/Credit Note Selected', 'Please select a receipt/credit note to delete', [
          { text: 'OK' },
        ]);
        return;
      }
      let data = this.selectedNote[0];
      let refno = data['Type'] == 'RT' ? data['Receipt No'] : data['Credit No'];

      const confirmDel = await this.$showAlert(
        'warning',
        `Deleting ${refno}`,
        `Are you sure you want to delete this record ?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (confirmDel == 'No') return;

      let temp = this.notesList;
      this.notesList = [];
      let updated = temp.filter((t) => (t['Type'] == 'RT' ? t['Receipt No'] != refno : t['Credit No'] != refno));
      this.$nextTick(() => {
        this.notesList = updated;
        this.updateNotesIndex();
        this.updateRefundTotal();
        this.updateRefundForeignTotal();
        if (this.notesList.length == 0) {
          this.refundCurrency = '';
        }
      });
    },

    updateNotesIndex: function () {
      let orderedList = this.notesList.map((n, index) => ({
        ...n,
        '#': index + 1,
      }));
      this.notesList = orderedList;
    },

    saveNoteClicked: async function () {
      if (!this.refundCurrency) {
        this.refundCurrency = this.noteCurrency;
      } else {
        if (this.noteCurrency['Currency Code'] !== this.refundCurrency['Currency Code'].trim()) {
          await this.$showAlert(
            'error',
            'Different Currency Selected',
            `Cannot add transaction with currency other than ${this.refundCurrency['Currency Code']}`,
          );
          return;
        }
      }

      let error = false;
      if (this.offsetFAmount.trim().length == 0) {
        this.offsetFAmountErr = 'Offset Amount is Required!';
        error = true;
      } else if (Number(this.offsetFAmount) <= 0) {
        this.offsetFAmountErr = 'Offset Amount must be more than 0!';
        error = true;
      } else if (!this.offsetFAmount.match(/^\d*\.?\d*$/g)) {
        this.offsetFAmountErr = 'Offset Amount cannot exceed 14 whole digits and 2 decimals!';
        error = true;
      } else {
        let parts = this.offsetFAmount.split('.');
        if (parts[0].length > 14 || parts[1].length > 2) {
          this.offsetFAmountErr = 'Offset Amount cannot exceed 14 whole digits and 2 decimals!';
          error = true;
        }
      }

      if (Number(this.offsetMYRAmount) == 0) {
        this.offsetMYRAmountErr = 'Offset Amount must be more than 0!';
        error = true;
      } else if (!this.offsetMYRAmount.match(/^\d*\.?\d*$/g)) {
        this.offsetMYRAmountErr = 'Offset Amount cannot exceed 14 whole digits and 2 decimals!';
        error = true;
      } else {
        let parts = this.offsetMYRAmount.split('.');
        if (parts[0].length > 14 || parts[1].length > 2) {
          this.offsetMYRAmountErr = 'Offset Amount cannot exceed 14 whole digits and 2 decimals!';
          error = true;
        }
      }

      if (error) return;

      let temp = this.notesList;
      this.notesList = [];

      let note = {
        action: 'N',
        '#': '',
        'Receipt No': this.selectedRcptCr['Type'] == 'RT' ? this.selectedRcptCr['Ref No'] : '',
        'Credit No': this.selectedRcptCr['Type'] == 'CN' ? this.selectedRcptCr['Ref No'] : '',
        'Offset Amount': Number(this.offsetMYRAmount).toFixed(2),
        'F.Offset Amount': Number(this.offsetFAmount).toFixed(2),
        Currency: this.noteCurrency['Currency Code'],
        'Ex. Rate': Number(this.noteExchgRate).toFixed(2),
        Remarks: '',
        'Inv No': '',
        Type: this.selectedRcptCr['Type'],
      };

      this.$nextTick(() => {
        temp.push(note);
        this.notesList = temp;
        this.updateNotesIndex();
        this.clearRefundDetails();
        this.updateRefundTotal();
        this.updateRefundForeignTotal();
      });
      this.showDetails = false;
    },

    cancelNoteClicked: function () {
      this.showDetails = false;
      this.clearRefundDetails();
    },

    custSelected: async function (val) {
      let discard = 'Discard';
      if (this.notesList.length > 0 || this.documentsList.length > 0) {
        discard = await this.$showAlert(
          'warning',
          'Discard Changes',
          "There's already data added in the tabs. Do you wish to discard the changes and change customer?",
          [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
        );
      } else if (this.showDetails) {
        discard = await this.promptDiscardReceiptCredit();
      }

      if (discard == 'Cancel') {
        this.selectedCustomer = this.previousSelectedCust;
        return;
      }

      if (val['Customer Code'] === null) {
        return;
      }

      this.clearRefundDetails();
      this.notesList = [];
      this.viewMode = false;
      this.$fullscreenLoading('Getting Receipt/Credit Notes');
      this.rcptCrList = await this.$apiCallV2(`/RefundNote/GetReceiptCreditListing?custid=${val['Customer Code']}`);
      this.$fullscreenLoading();
    },

    receiptCreditSelected: async function (val) {
      this.noteCurrency = this.currencyList.find((c) => c['Currency Code'] == val['Currency']);
      this.currFactor = this.noteCurrency['Factor'];
      this.noteExchgRate = val['Rate'];
      this.oriFAmount = val['F Balance'];
      this.offsetFAmount = val['F Balance'];
      this.oriMYRAmount = val['Balance'];
      this.offsetMYRAmount = val['Balance'];
    },

    tabClicked: async function (tabItem, tabIndex) {
      if (this.showDetails && tabIndex !== 0) {
        const discardChanges = await this.promptDiscardReceiptCredit();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.showDetails = false;
        this.clearRefundDetails();
      }
      this.currentTabIndex = tabIndex;
    },

    promptDiscardReceiptCredit: function () {
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'Receipt/Credit note details not added yet.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },

    updateFOffsetLossFocus: async function () {
      if (Number(this.offsetFAmount) > this.oriFAmount) {
        await this.$showAlert('error', 'Offset Amount Too Big', `Offset amount cannot be more than outstanding amount`);
        this.offsetFAmount = this.oriFAmount;
        return;
      }

      if (this.offsetFAmount.trim().length == 0) {
        this.offsetFAmount = '0.00';
      }
      const myr = ((Number(this.offsetFAmount) * Number(this.noteExchgRate)) / Number(this.currFactor)).toFixed(2);
      this.offsetMYRAmount = isNaN(myr) ? '0.00' : myr;
      this.offsetFAmount = parseFloat(this.offsetFAmount).toFixed(2); // always set value to 2 decimal place
    },

    updateOffsetLossFocus: async function () {
      if (parseFloat(this.offsetMYRAmount) > this.oriMYRAmount) {
        await this.$showAlert('error', 'Offset Amount Too Big', `Offset amount cannot be more than outstanding amount`);
        this.offsetMYRAmount = this.oriMYRAmount;
        return;
      }
      if (this.offsetMYRAmount.trim().length == 0) {
        this.offsetMYRAmount = '0.00';
      }
      const foreign = ((Number(this.offsetMYRAmount) / Number(this.noteExchgRate)) * Number(this.currFactor)).toFixed(
        2,
      );
      this.offsetFAmount = isNaN(foreign) ? '0.00' : foreign;
      this.offsetMYRAmount = parseFloat(this.offsetMYRAmount).toFixed(2);
    },
    //#endregion

    //#region Attachments Tab methods
    handleFileUpload: async function (file) {
      if (file.length == 0) {
        this.documents['docName'] = '';
        this.documents['fileType'] = '';
        this.documents['attachmentFile'];
      } else {
        this.$fullscreenLoading('Adding File');
        this.documents['docName'] = file[0].name;
        this.documents['fileType'] = file[0].name.substring(file[0].name.indexOf('.'));
        let tempAtt = await file[0].toBase64();
        this.documents['attachmentFile'] = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.$fullscreenLoading();
      }
    },

    attachmentBtnsClicked: async function (btn) {
      this.documents;
      let temp;
      let highestID;
      let document = null;

      switch (btn) {
        case 'Add':
          if (!this.documents['docName']) {
            this.attachmentsRequiredErrorMessage = 'Please attach at least one file';
            return;
          }

          let duplicate = false;
          this.documentsList.forEach((d) => {
            if (d['File Name'] === this.documents['docName']) duplicate = true;
          });

          if (duplicate) {
            await this.$showAlert('error', 'Selected File already exist!', 'Please select other attachment');
            return;
          }

          temp = [...this.documentsList];
          this.documentsList = [];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          document = {
            Action: 'N',
            'File Name': !this.documents['docName'] ? '' : this.documents['docName'],
            'Doc Remark': !this.documents['remarks'] ? '' : this.documents['remarks'],
            'Doc Image': !this.documents['attachmentFile'] ? '' : this.documents['attachmentFile'],
            'Document GUID': !this.documents['docGuid'] ? '' : this.documents['docGuid'],
            'File Type': !this.documents['fileType'] ? '' : this.documents['fileType'],
            ID: !this.documents['docID'] ? ++highestID : this.documents['docID'],
            'Doc Date': new Date().formatString('yyyy-MM-dd HH:mm'),
            _attachmentAction: 'New',
          };

          this.$nextTick(() => {
            temp.push(document);
            this.documentsList = temp;
          });
          this.showAttachmentsPopup = false;
          break;

        case 'Cancel':
          this.showAttachmentsPopup = false;
          break;
      }
    },

    deleteDocClicked: async function () {
      if (this.selectedDoc.length == 0) {
        this.$showAlert('error', 'No Document Selected', 'Please select at least 1 document to delete.');
        return;
      }
      let confirmDelete = await this.$showAlert(
        'warning',
        'Deleting Attachment',
        `Are you sure you want to delete this attachment ?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (confirmDelete == 'No') return;

      // Can find document by index because no edit mode, all in new mode only
      let filteredDocList = this.documentsList.filter((d) => d['ID'] !== this.selectedDoc[0]['ID']);
      this.documentsList = [];

      this.$nextTick(() => {
        this.documentsList = filteredDocList;
        this.selectedDoc = [];
      });
    },

    viewDocClicked: async function (btn, data) {
      // if (this.selectedDoc.length == 0) {
      //   this.$showAlert("error", "No Attachment Selected", "Please select an attachment to view", [{text: "OK"}]);
      //   return;
      // }
      await this.projectAttachment({
        'File Name': data['File Name'],
        'MIME Type': null,
        'File Data': data['Doc Image'],
      });
    },

    attachmentIsProjectable: function () {
      if (!this.attachmentProjectorFileName || !this.attachmentProjectorType) {
        // if either of these are blank
        return false;
      }

      const filenameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = filenameSplit[filenameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },

    projectAttachment: async function (file) {
      // file could be a file object or a JS object
      if (!file && process.env['NODE_ENV'] == 'development') {
        console.error('No file was passed to projectAttachment. Check for logic errors');
        console.trace();
        return;
      }

      this.$fullscreenLoading('Preparing file');
      if (file instanceof File) {
        // file object passed
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        // JS object passed
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/RefundNote/GetMIMEType?filename=${this.attachmentProjectorFileName}`));

        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );
        // if File Data is a data URL instead of just base64 data, then remove the prefix, MIME type and base64 indicator before converting to a string
        let untypedByteArray = [];
        for (let index = 0; index < byteCharacters.length; index++) {
          untypedByteArray.push(byteCharacters.charCodeAt(index));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], { type: this.attachmentProjectorType });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.isProjectable = this.attachmentIsProjectable();
      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },

    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.selectedDoc[0]['File Name'];
      anchor.click();
    },

    closeProjector: function (pointerEvent) {
      this.attachmentProjectorType = '';
      this.attachmentProjectorData = '';
      this.showAttachmentProjector = false;
    },
    //#endregion

    convertThreeDigit: function (num) {
      // Function to convert a three-digit number to words
      // Array of words representing numbers from 0 to 19
      var words = [
        '',
        'ONE',
        'TWO',
        'THREE',
        'FOUR',
        'FIVE',
        'SIX',
        'SEVEN',
        'EIGHT',
        'NINE',
        'TEN',
        'ELEVEN',
        'TWELVE',
        'THIRTEEN',
        'FOURTEEN',
        'FIFTEEN',
        'SIXTEEN',
        'SEVENTEEN',
        'EIGHTEEN',
        'NINETEEN',
      ];

      // Array of words representing multiples of 10 from 20 to 90
      var tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

      if (num == 0) return '';

      var hundreds = Math.floor(num / 100);
      var remainder = num % 100;

      var result = '';
      if (hundreds > 0) {
        result += words[hundreds] + ' HUNDREDS ';
      }

      if (remainder > 0) {
        if (remainder < 20) {
          result += words[remainder];
        } else {
          var tensDigit = Math.floor(remainder / 10);
          var onesDigit = remainder % 10;
          result += tens[tensDigit];
          if (onesDigit > 0) {
            result += '-' + words[onesDigit];
          }
        }
      }

      return result.trim();
    },

    convertToWords: function (num) {
      // Function to recursively convert a number to words
      // Array of words representing powers of 10 (e.g., thousand, million, billion)
      var scales = ['', 'THOUSAND', 'MILLION', 'BILLION', 'TRILLION', 'QUADRILLION'];
      if (num == 0) return 'ZERO';

      var result = '';
      var scaleIndex = 0;

      while (num > 0) {
        var threeDigits = num % 1000;
        if (threeDigits > 0) {
          result = this.convertThreeDigit(threeDigits) + ' ' + scales[scaleIndex] + ' ' + result;
        }
        num = Math.floor(num / 1000);
        scaleIndex++;
      }

      return result.trim();
    },

    convertCurrencyToWords: function (cur) {
      cur = Number(cur).toFixed(2);
      var parts = cur.split('.');
      var wholePart = parseInt(parts[0]);
      var fractionalPart = parseInt(parts[1]) || 0;

      // Convert whole part to words
      var wholeWords = this.convertToWords(wholePart);
      var fractionalWords = this.convertToWords(fractionalPart);

      // Construct the final result
      const currName = this.refundCurrency?.['Description'] ?? 'MALAYSIAN RINGGIT';
      var result = currName + ' ' + wholeWords;
      if (fractionalPart > 0) {
        result += ' AND ' + fractionalWords + ' CENTS';
      }

      return result;
    },

    updateChequeNoForCsLg: function () {
      if (!this.viewMode) {
        if (this.selectedPaymentType && ['CS', 'LG'].includes(this.selectedPaymentType['Payment Type'])) {
          this.chequeNo = '**********';
          this.chequeNoDisable = true;
        } else {
          this.chequeNo = '';
          this.chequeNoDisable = false;
        }
      }
    },
  },

  async mounted() {
    this.$fullscreenLoading('Getting Refund Notes');
    this.searchFilterItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'REFUNDINQUIRY_SEARCH' });
    await this.refreshClicked();

    this.$fullscreenLoading('Getting Customers');
    this.customersList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'customer' });

    this.$fullscreenLoading('Getting Payment Types');
    this.paymentTypeList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'paymenttype' });

    this.$fullscreenLoading('Getting Banks');
    this.bankList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'bank' });

    this.$fullscreenLoading('Getting Currency');
    this.currencyList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'currency' });

    this.$fullscreenLoading();
  },

  watch: {
    selectedCustomer: function (newVal, oldVal) {
      this.previousSelectedCust = oldVal;
    },

    totalFAmount: function (val) {
      this.totalInWords = this.convertCurrencyToWords(val.toString().replaceAll(',', ''));
    },

    showAttachmentsPopup: function (val) {
      if (val) {
        this.attachmentsRequiredErrorMessage = '';
        this.documents = {
          docName: '',
          attachmentFile: [],
          fileType: '',
          docGuid: '',
          remarks: '',
          docID: 0,
        };
      }
    },

    chequeNo: function (val) {
      if (val.trim().length > 0 && !this.chequeNoDisable) {
        this.bankChequeRequired = true;
      } else {
        this.bankChequeRequired = false;
      }
    },

    selectedPaymentType: function (val) {
      if (this.createChequeChecked) this.updateChequeNoForCsLg();
    },

    createChequeChecked: function (val) {
      if (!val && !this.viewMode) {
        this.chequeNo = '';
        this.chequeDate = null;
      } else {
        this.updateChequeNoForCsLg();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
@import '@/css/FIN.scss';

.refund-note {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    > .filter-bar,
    > .table-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .filter-bar {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-description {
        padding-right: 8px;
      }

      > .button-filter-component {
        margin-right: 5px;
        position: relative;

        .filter-popup {
          display: flex;
          flex-direction: column;

          > button {
            margin-top: 5px;
            width: fit-content;
            align-self: flex-end;
          }
        }
      }
    }

    > .table-section {
      height: calc(100% - 38px - 5px); // deduct height of top-section and margin
      margin-top: 5px;
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }
}

.first-section {
  display: flex;
  width: 100%;

  .top-row-column {
    width: 100%;
    margin-right: 50px;
  }
}

.summary-container {
  display: flex;
  flex-direction: row;
  width: fit-content;

  > :nth-child(1) {
    margin-right: 5px;
  }
}

.table-container {
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 5px;
  margin-top: 3px;
  height: 100%;

  > .tabulator-title {
    display: flex;
    align-items: center;
    margin-bottom: 5px;

    > button {
      margin-left: 3px;
    }
  }
}

.foreign-currency-column {
  padding: 0px 5px !important;
}

.foreign-currency-background {
  > .background-left {
    width: calc((100% / 3) + 4px);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    width: calc((100% / 3) + 4px);
    border-left: 3px solid white;
  }
}

.total-block {
  border: 1px solid rgba(128, 128, 128, 0.5);
  max-width: 200px;
  min-width: 200px;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
  padding: 3px;
}
</style>
